import React from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AdminSurface from '../../../../components/admin/AdminSurface';
import type { MissingGame } from '../../../../types/features/games';
import {
  adminCombineSx,
  adminEmptyStateSx,
  adminTableSx,
} from '../../../../styles/adminComponentStyles';
import { adminColors, adminTypography } from '../../../../styles/adminDesignTokens';
import { formatDateTime, formatPluGenerationTooltip, valueWithConflicts } from '../utils/formatters/missingGamesFormatters';

interface MissingGamesTableProps {
  games: MissingGame[];
  loading: boolean;
  onAddGame: (game: MissingGame) => void;
}

const addGameActionSx = {
  appearance: 'none',
  alignItems: 'center',
  backgroundColor: 'transparent',
  border: 0,
  color: adminColors.primary,
  cursor: 'pointer',
  display: 'inline-flex',
  fontFamily: 'inherit',
  fontSize: adminTypography.body.fontSize,
  fontWeight: adminTypography.body.fontWeight,
  gap: 0.5,
  justifyContent: 'center',
  lineHeight: adminTypography.body.lineHeight,
  minWidth: 0,
  p: 0,
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: 'transparent',
    color: adminColors.primaryHover,
  },
  '&:focus-visible': {
    borderRadius: '2px',
    outline: `2px solid ${adminColors.focusRing}`,
    outlineOffset: '2px',
  },
  '& .MuiSvgIcon-root': {
    flexShrink: 0,
    fontSize: 18,
  },
} as const;

const addGameActionLabelSx = {
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
} as const;

const compactChipSx = {
  '& .MuiChip-label': {
    px: 0.75,
  },
} as const;

const missingGamesTableSx = adminCombineSx(adminTableSx, {
  minWidth: 1120,
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    padding: '6px 8px !important',
  },
  '& .MuiTableCell-head': {
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    height: 38,
  },
  '& .MuiTableCell-body': {
    py: 0.5,
  },
  '& th:nth-of-type(1), & td:nth-of-type(1)': { width: 56 },
  '& th:nth-of-type(2), & td:nth-of-type(2)': { width: 68 },
  '& th:nth-of-type(3), & td:nth-of-type(3)': { width: 112 },
  '& th:nth-of-type(4), & td:nth-of-type(4)': { width: 128 },
  '& th:nth-of-type(5), & td:nth-of-type(5)': { width: 128 },
  '& th:nth-of-type(6), & td:nth-of-type(6)': { width: 104 },
  '& th:nth-of-type(7), & td:nth-of-type(7)': { width: 78 },
  '& th:nth-of-type(8), & td:nth-of-type(8)': { width: 110 },
  '& th:nth-of-type(9), & td:nth-of-type(9)': { width: 136 },
  '& th:nth-of-type(10), & td:nth-of-type(10)': { width: 96 },
  '& th:nth-of-type(11), & td:nth-of-type(11)': { width: 104 },
});

const missingGamesTableHeadSx = {
  '& th': {
    padding: '6px 8px !important',
    textTransform: 'uppercase',
  },
} as const;

const MissingGamesTable: React.FC<MissingGamesTableProps> = ({ games, loading, onAddGame }) => (
  <AdminSurface sx={{ boxShadow: '0 2px 8px rgb(34 43 69 / 8%)', overflow: 'hidden' }}>
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table
        size="small"
        aria-label="missing games table"
        sx={missingGamesTableSx}
      >
        <TableHead sx={missingGamesTableHeadSx}>
          <TableRow>
            <TableCell>State</TableCell>
            <TableCell>Game</TableCell>
            <TableCell>First report</TableCell>
            <TableCell>First seen</TableCell>
            <TableCell>Last seen</TableCell>
            <TableCell>Stores / packs</TableCell>
            <TableCell>Price</TableCell>
            <TableCell>Tickets / pack</TableCell>
            <TableCell>Generated PLU</TableCell>
            <TableCell>Data</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={11} sx={{ border: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress size={28} />
                </Box>
              </TableCell>
            </TableRow>
          ) : games.length === 0 ? (
            <TableRow>
              <TableCell colSpan={11} sx={{ border: 0, p: 1.5 }}>
                <Box sx={adminCombineSx(adminEmptyStateSx, { border: 0, minHeight: 96 })}>
                  <Typography className="AdminEmptyState-title">No missing games</Typography>
                  <Typography className="AdminEmptyState-copy">
                    All games reported by linked active stores in the last two months are in the games table.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : games.map((game) => {
            const storeNames = game.stores.map((store) => `${store.name}${store.city ? ` (${store.city})` : ''}`).join(', ');
            const price = game.price_point ? `$${game.price_point}` : '—';
            const tickets = game.tickets_per_pack?.toString() || '—';

            return (
              <TableRow key={`${game.state}-${game.game_id}`} hover>
                <TableCell>{game.state}</TableCell>
                <TableCell>{game.game_id}</TableCell>
                <TableCell>{game.first_report_date}</TableCell>
                <TableCell>{formatDateTime(game.first_seen_at)}</TableCell>
                <TableCell>{formatDateTime(game.last_seen_at)}</TableCell>
                <TableCell>
                  <Tooltip title={storeNames || 'No stores'} arrow>
                    <span>{game.store_count} / {game.pack_count}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {valueWithConflicts(price, game.price_point_values.map((value) => `$${value}`))}
                </TableCell>
                <TableCell>{valueWithConflicts(tickets, game.tickets_per_pack_values)}</TableCell>
                <TableCell>
                  {game.generated_plu ? (
                    <Tooltip title={formatPluGenerationTooltip(game)} arrow slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}>
                      <Chip label={game.generated_plu} size="small" variant="outlined" color="primary" sx={compactChipSx} />
                    </Tooltip>
                  ) : (game.plu_generation?.reason || '—')}
                </TableCell>
                <TableCell>
                  <Chip
                    label={game.has_conflict ? 'Conflict' : 'Consistent'}
                    size="small"
                    color={game.has_conflict ? 'error' : 'success'}
                    variant={game.has_conflict ? 'filled' : 'outlined'}
                    sx={compactChipSx}
                  />
                </TableCell>
                <TableCell>
                  <Box
                    aria-label="Add Games"
                    component="button"
                    type="button"
                    onClick={() => onAddGame(game)}
                    sx={addGameActionSx}
                  >
                    <AddRoundedIcon aria-hidden="true" />
                    <Box component="span" sx={addGameActionLabelSx}>
                      Add Games
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </AdminSurface>
);

export default React.memo(MissingGamesTable);
